import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import ModalForms from '../Shared/Modals/index.js';
import SchoolIcon from '@mui/icons-material/School';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import HistoryIcon from '@mui/icons-material/History';
import QuizIcon from '@mui/icons-material/Quiz';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import FunctionsIcon from '@mui/icons-material/Functions';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

interface HelpDialogProps {
  open: boolean;
  onClose: () => void;
}

/* ── tiny helpers ─────────────────────────────────────────────────────────── */

const SectionHeader = ({
  icon,
  color,
  title,
}: {
  icon: React.ReactNode;
  color: string;
  title: string;
}) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      mb: 1,
      pb: 0.75,
      borderBottom: '1px solid #f0f0f0',
    }}
  >
    <Box
      sx={{
        width: 28,
        height: 28,
        borderRadius: 1.5,
        bgcolor: `${color}18`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        color,
      }}
    >
      {icon}
    </Box>
    <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#1a1a2e' }}>
      {title}
    </Typography>
  </Box>
);

const Bullet = ({ color, children }: { color: string; children: React.ReactNode }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.6 }}>
    <Box
      sx={{
        width: 5,
        height: 5,
        borderRadius: '50%',
        bgcolor: color,
        mt: '7px',
        flexShrink: 0,
      }}
    />
    <Typography sx={{ fontSize: 12.5, color: '#4a4a6a', lineHeight: 1.6 }}>
      {children}
    </Typography>
  </Box>
);

const ModeChip = ({
  icon,
  label,
  color,
}: {
  icon: React.ReactNode;
  label: string;
  color: string;
}) => (
  <Box
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.5,
      px: 1,
      py: 0.4,
      borderRadius: 1.5,
      border: '1px solid',
      borderColor: `${color}33`,
      bgcolor: `${color}0d`,
      mr: 0.75,
      mb: 0.75,
    }}
  >
    <Box sx={{ color, display: 'flex', alignItems: 'center', '& svg': { fontSize: 13 } }}>
      {icon}
    </Box>
    <Typography sx={{ fontSize: 11, fontWeight: 600, color }}>{label}</Typography>
  </Box>
);

/* ── main component ───────────────────────────────────────────────────────── */

const HelpDialog: React.FC<HelpDialogProps> = ({ open, onClose }) => {
  const modalBody = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

      {/* 1 — Context */}
      <Box>
        <SectionHeader
          icon={<AccountTreeIcon sx={{ fontSize: 15 }} />}
          color="#667eea"
          title="1 · Select Your Learning Context"
        />
        <Bullet color="#667eea">
          Choose a <strong>Subject</strong> from the sidebar (e.g. Physics, Mathematics).
        </Bullet>
        <Bullet color="#667eea">
          Pick a <strong>Topic</strong> and <strong>Subtopic</strong> to narrow your focus.
        </Bullet>
        <Bullet color="#667eea">
          The AI tailors every response to your exact class level and topic.
        </Bullet>
      </Box>

      {/* 2 — Ask */}
      <Box>
        <SectionHeader
          icon={<AutoAwesomeIcon sx={{ fontSize: 15 }} />}
          color="#10b981"
          title="2 · Ask Questions"
        />
        <Bullet color="#10b981">
          Type any question and press <strong>Enter</strong> or the Send button.
        </Bullet>
        <Bullet color="#10b981">
          Tap the <strong>🎤 microphone</strong> to dictate your question by voice.
        </Bullet>
        <Bullet color="#10b981">
          Attach photos of textbook pages or handwritten problems using the <strong>📎 icon</strong>.
        </Bullet>
        <Bullet color="#10b981">
          Click predefined question cards to jump-start a topic instantly.
        </Bullet>

        {/* Instruction modes */}
        <Box sx={{ mt: 1 }}>
          <Typography sx={{ fontSize: 11.5, fontWeight: 600, color: '#666', mb: 0.75 }}>
            Instruction Mode — choose how the AI responds:
          </Typography>
          <ModeChip icon={<InfoOutlinedIcon />} label="Default" color="#667eea" />
          <ModeChip icon={<SchoolIcon />} label="Step-by-Step Solution" color="#f59e0b" />
          <ModeChip icon={<LightbulbIcon />} label="Step-by-Step Reasoning" color="#8b5cf6" />
        </Box>
      </Box>

      {/* 3 — Diagrams */}
      <Box>
        <SectionHeader
          icon={<AutoAwesomeIcon sx={{ fontSize: 15 }} />}
          color="#f59e0b"
          title="3 · AI-Generated Diagrams & Images"
        />
        <Bullet color="#f59e0b">
          The AI auto-generates <strong>visual diagrams</strong> (flowcharts, mind maps) when helpful.
        </Bullet>
        <Bullet color="#f59e0b">
          Diagrams appear as compact preview cards — <strong>click to expand</strong> full size.
        </Bullet>
        <Bullet color="#f59e0b">
          Use the <strong>⬇ Download</strong> button on any diagram or image to save as PNG.
        </Bullet>
        <Bullet color="#f59e0b">
          Generated images also open in a popup with full download support.
        </Bullet>
      </Box>

      {/* 4 — Practice */}
      <Box>
        <SectionHeader
          icon={<QuizIcon sx={{ fontSize: 15 }} />}
          color="#ef4444"
          title="4 · Practice & Test Yourself"
        />
        <Bullet color="#ef4444">
          Click <strong>"Practice Test"</strong> after any answer to generate a test on that topic.
        </Bullet>
        <Bullet color="#ef4444">
          Choose difficulty — <strong>Easy</strong>, <strong>Medium</strong>, or <strong>Hard</strong>.
        </Bullet>
        <Bullet color="#ef4444">
          Explore <strong>"You Should Also Know"</strong> concept questions below each answer.
        </Bullet>
        <Bullet color="#ef4444">
          Click <strong>"New Query"</strong> to start a fresh conversation on the same subject.
        </Bullet>
      </Box>

      {/* 5 — Math */}
      <Box>
        <SectionHeader
          icon={<FunctionsIcon sx={{ fontSize: 15 }} />}
          color="#8b5cf6"
          title="5 · Scientific Keyboard & Math Input"
        />
        <Bullet color="#8b5cf6">
          Tap the <strong>Σ</strong> button to open the scientific symbol keyboard.
        </Bullet>
        <Bullet color="#8b5cf6">
          Insert Greek letters, operators, fractions and more with one click.
        </Bullet>
        <Bullet color="#8b5cf6">
          AI responses support full <strong>LaTeX / KaTeX</strong> math rendering automatically.
        </Bullet>
      </Box>

      {/* 6 — History */}
      <Box>
        <SectionHeader
          icon={<HistoryIcon sx={{ fontSize: 15 }} />}
          color="#0ea5e9"
          title="6 · Chat History"
        />
        <Bullet color="#0ea5e9">
          All conversations are saved automatically in the sidebar.
        </Bullet>
        <Bullet color="#0ea5e9">
          Switch between <strong>Topic-wise</strong> and <strong>All Chats</strong> tabs in the sidebar.
        </Bullet>
        <Bullet color="#0ea5e9">
          Click any past conversation to reload it, including all diagrams and images.
        </Bullet>
        <Bullet color="#0ea5e9">
          If a response arrives in another chat, a notification banner will appear — click <strong>"View"</strong> to jump to it.
        </Bullet>
      </Box>

      {/* Pro Tips */}
      <Box
        sx={{
          borderRadius: 2,
          border: '1px solid #fde68a',
          bgcolor: '#fffbeb',
          px: 2,
          py: 1.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
          <TipsAndUpdatesIcon sx={{ fontSize: 15, color: '#d97706' }} />
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#92400e' }}>
            Pro Tips
          </Typography>
        </Box>
        <Bullet color="#d97706">
          Ask specific questions — the more context you give, the better the answer.
        </Bullet>
        <Bullet color="#d97706">
          Switch to "Reasoning" mode when you want to understand the <em>why</em>, not just the answer.
        </Bullet>
        <Bullet color="#d97706">
          Attach a photo of a tricky problem for instant image-based help.
        </Bullet>
        <Bullet color="#d97706">
          Use concept check questions after each answer to deepen your understanding.
        </Bullet>
      </Box>

    </Box>
  );

  const modalFooter = (
    <Button
      onClick={onClose}
      variant="contained"
      fullWidth
      sx={{
        textTransform: 'none',
        fontWeight: 600,
        borderRadius: 2,
        bgcolor: '#667eea',
        boxShadow: 'none',
        '&:hover': { bgcolor: '#5568d3', boxShadow: 'none' },
      }}
    >
      Got it, let's learn! 🚀
    </Button>
  );

  return (
    <ModalForms
      show={open}
      handleClose={onClose}
      modalHeader="How to Use Concept Builder"
      modalBody={modalBody}
      modalFooter={modalFooter}
      size="md"
    />
  );
};

export default HelpDialog;